import React, { useState } from 'react';
import {
  Image,
  ImageResizeMode,
  ImageSourcePropType,
  ImageStyle,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import Illustration, {
  IllustrationSourceType,
} from 'shared/components/vxl/Illustration/Illustration';
import theme from 'shared/styles/theme';

export interface IconAvatarProps {
  /**
   * Size of the Icon and the Image
   */
  variant: 'LG' | 'MD' | 'SM';
  // If you would like to use an icon, provide an iconKey. This will take precedence over imageSource if both are provided.
  iconKey?: string;
  /** The accessibility label for the icon. */
  iconAccessibilityLabel?: string;
  /** The background color of the SVG icon or illustration. */
  backgroundColor?: string;
  /** The color of the icon. */
  iconColor?: string;
  /** The remote url of the image or the local resource image. */
  imageSource?: ImageSourcePropType;
  /** To pass your custom default icon instead of if IconKey not found */
  defaultPlaceholderIcon?: string;
  /** The image style for image */
  imageStyle?: StyleProp<ImageStyle> | null;
  /** The resize mode for image */
  resizeMode?: ImageResizeMode;
  /** The illustration key is used to render a specific illustration. The illustration key is a string from a predefined list of values.*/
  illustrationKey?: IllustrationSourceType;
}

const getImageStyle = (variant: string) => {
  switch (variant) {
    case 'LG':
      return styles.largeImageSizeStyle;
    case 'SM':
      return styles.smallImageSizeStyle;
    case 'MD':
    default:
      return styles.mediumImageSizeStyle;
  }
};

const getContainerStyle = (variant: string) => {
  switch (variant) {
    case 'MD':
    default:
      return styles.mediumSizeStyle;
    case 'LG':
      return styles.largeSizeStyle;
  }
};

// Function to create styles dynamically
const createStyles = (
  backgroundColor: string,
): { backGroundColorStyle: StyleProp<ViewStyle> } => {
  /* eslint-disable react-native/no-unused-styles -- dynamic style keys require disabling the rule */
  return StyleSheet.create({
    backGroundColorStyle: {
      backgroundColor,
    },
  });
  /* eslint-enable react-native/no-unused-styles */
};

const IconAvatar = ({
  /**
   * Size of the Icon and the Image
   */
  variant = 'MD',
  /** The icon key is used to render a specific icon. The icon key is a string from a predefined list of values. */
  iconKey,
  /** The accessibility label for the icon. */
  iconAccessibilityLabel,
  /** The remote url of the image or the local resource image. */
  imageSource,
  /** The background color of the SVG icon or illustration. */
  backgroundColor = theme.color.background.tertiary,
  /** The color of the icon. */
  iconColor = theme.color.text.primary,
  /** The default placeholder icon, we can pass our custom as well */
  defaultPlaceholderIcon = 'PLACEHOLDER',
  /** The image style for image */
  imageStyle,
  /** The resize mode for image */
  resizeMode = 'cover',
  /** The illustration key is used to render a specific illustration. The illustration key is a string from a predefined list of values.*/
  illustrationKey,
}: IconAvatarProps) => {
  const [imageLoadError, setImageLoadError] = useState(false);
  const renderIconKey = imageLoadError ? defaultPlaceholderIcon : iconKey;

  if (iconKey) {
    const iconSize = variant === 'LG' ? 36 : theme.spacing.l;

    // Generate styles dynamically
    const generatedStyles = createStyles(backgroundColor);

    return (
      <View
        style={[
          styles.iconWrapper,
          getContainerStyle(variant),
          generatedStyles.backGroundColorStyle,
        ]}
      >
        <Icon
          iconKey={renderIconKey}
          iconSize={iconSize}
          iconColor={iconColor}
          iconAccessibilityLabel={iconAccessibilityLabel}
        />
      </View>
    );
  }

  if (illustrationKey) {
    const generatedStyles = createStyles(backgroundColor);
    return (
      <View
        style={[
          styles.illustrationWrapper,
          getContainerStyle(variant),
          generatedStyles.backGroundColorStyle,
        ]}
      >
        <Illustration illustration={illustrationKey} />
      </View>
    );
  }

  return (
    <Image
      source={imageSource}
      style={[styles.logo, imageStyle ? imageStyle : getImageStyle(variant)]}
      alt="company-logo"
      role="img"
      accessible
      resizeMode={resizeMode}
      onError={() => setImageLoadError(true)}
    />
  );
};

const styles = StyleSheet.create({
  iconWrapper: {
    alignItems: 'flex-start',
    backgroundColor: theme.color.background.tertiary,
    borderRadius: theme.spacing.xs,
    display: 'flex',
    gap: 10,
  },
  illustrationWrapper: {
    alignItems: 'center',
    borderRadius: 4,
    justifyContent: 'center',
  },
  largeImageSizeStyle: {
    height: 56,
    width: 56,
  },
  largeSizeStyle: {
    height: 56,
    padding: 10,
    width: 56,
  },
  logo: {
    borderRadius: 4,
  },
  mediumImageSizeStyle: {
    height: 40,
    width: 40,
  },
  mediumSizeStyle: {
    alignItems: 'center',
    height: 40,
    justifyContent: 'center',
    width: 40,
  },
  smallImageSizeStyle: {
    height: 24,
    width: 24,
  },
});

export default IconAvatar;
